import { useEffect, useMemo, useState, useContext } from "react";
import { api, formatarErroApi } from "@/api";
import { LayoutAutenticado } from "@/componentes/LayoutAutenticado";
import { CartaoJogo } from "@/componentes/CartaoJogo";
import { GerenciadorJogosDragDrop } from "@/componentes/GerenciadorJogosDragDrop";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { AutenticacaoContexto } from "@/contextos/AutenticacaoContexto";

export default function GerenciarJogos() {
  const { usuario } = useContext(AutenticacaoContexto);
  const [jogos, setJogos] = useState([]);
  const [selecoes, setSelecoes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalResultado, setModalResultado] = useState(null);
  const [modalEdicao, setModalEdicao] = useState(null);
  
  const ehSuperAdmin = usuario?.perfil === "SUPER_ADMIN";

  const carregar = async () => {
    const [j, s, g] = await Promise.all([
      api.get("/jogos"),
      api.get("/selecoes"),
      api.get("/grupos"),
    ]);
    setJogos(j.data);
    setSelecoes(s.data);
    setGrupos(g.data);
  };
  useEffect(() => { carregar(); }, []);

  const recalcular = async () => {
    try {
      await api.post("/resultados/recalcular");
      toast.success("Pontuação recalculada");
      carregar();
    } catch (e) { toast.error(formatarErroApi(e)); }
  };

  const deletarJogo = async (jogo) => {
    try {
      await api.delete(`/jogos/${jogo.id}`);
      toast.success("Partida deletada com sucesso");
      carregar();
    } catch (e) { toast.error(formatarErroApi(e)); }
  };

  const editarJogo = (jogo) => {
    setModalEdicao(jogo);
  };

  return (
    <LayoutAutenticado>
      <div data-testid="pagina-admin-jogos">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Operação</div>
            <h1 className="font-heading text-4xl font-bold uppercase mt-1">Jogos & Resultados</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={recalcular}
              className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest border border-white/10 hover:bg-white/5"
              data-testid="btn-recalcular"
            >Recalcular Tudo</button>
            <button
              onClick={() => setModalNovo(true)}
              className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest bg-verdebrasil hover:bg-verdebrasil-dark flex items-center gap-2"
              data-testid="btn-novo-jogo"
            >
              <Plus className="w-4 h-4" /> Novo Jogo
            </button>
          </div>
        </div>

        {jogos.length === 0 ? (
          <div className="surface-card rounded-2xl p-10 text-center text-white/50">
            Nenhum jogo cadastrado ainda. Use o botão "Novo Jogo".
          </div>
        ) : ehSuperAdmin ? (
          <GerenciadorJogosDragDrop
            jogos={jogos}
            modo="admin"
            onPalpitar={(jogo) => setModalResultado(jogo)}
            onEditar={editarJogo}
            onDeletar={deletarJogo}
          />
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {jogos.map((j) => (
              <CartaoJogo
                key={j.id}
                jogo={j}
                modo="admin"
                onPalpitar={(jogo) => setModalResultado(jogo)}
              />
            ))}
          </div>
        )}
      </div>

      {modalNovo && (
        <ModalNovoJogo
          selecoes={selecoes}
          grupos={grupos}
          onFechar={() => setModalNovo(false)}
          onSalvo={() => { setModalNovo(false); carregar(); }}
        />
      )}
      {modalResultado && (
        <ModalResultado
          jogo={modalResultado}
          onFechar={() => setModalResultado(null)}
          onSalvo={() => { setModalResultado(null); carregar(); }}
        />
      )}
      {modalEdicao && (
        <ModalEditarJogo
          jogo={modalEdicao}
          selecoes={selecoes}
          grupos={grupos}
          onFechar={() => setModalEdicao(null)}
          onSalvo={() => { setModalEdicao(null); carregar(); }}
        />
      )}
    </LayoutAutenticado>
  );
}

function ModalNovoJogo({ selecoes, grupos, onFechar, onSalvo }) {
  const gruposOrdenados = useMemo(() => {
    return [...grupos].sort((a, b) => {
      const nomeA = a.nome.replace("Grupo ", "").trim();
      const nomeB = b.nome.replace("Grupo ", "").trim();
      return nomeA.localeCompare(nomeB);
    });
  }, [grupos]);

  const [form, setForm] = useState({
    grupoId: gruposOrdenados[0]?.id || "",
    rodada: 1,
    selecaoMandanteId: "",
    selecaoVisitanteId: "",
    dataHoraJogo: "",
  });
  const [enviando, setEnviando] = useState(false);

  const selecoesGrupo = useMemo(
    () => selecoes.filter((s) => !form.grupoId || s.grupoId === form.grupoId),
    [selecoes, form.grupoId]
  );

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      // O input datetime-local retorna YYYY-MM-DDTHH:mm em horário local (Brasília)
      // Enviar como ISO string com timezone Brasília (UTC-3)
      const [dataParte, horaParte] = form.dataHoraJogo.split("T");
      const [ano, mes, dia] = dataParte.split("-");
      const [hora, minuto] = horaParte.split(":");
      
      // Criar ISO string em formato Brasília (UTC-3)
      const isoString = `${ano}-${mes}-${dia}T${hora}:${minuto}:00-03:00`;
      
      await api.post("/jogos", {
        grupoId: form.grupoId,
        rodada: Number(form.rodada),
        selecaoMandanteId: form.selecaoMandanteId,
        selecaoVisitanteId: form.selecaoVisitanteId,
        dataHoraJogo: isoString,
      });
      toast.success("Jogo criado");
      onSalvo();
    } catch (e) { toast.error(formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-lg w-full relative space-y-4" data-testid="modal-novo-jogo">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <h2 className="font-heading uppercase font-bold text-2xl">Novo Jogo</h2>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Grupo</label>
            <select
              value={form.grupoId}
              onChange={(e) => setForm({ ...form, grupoId: e.target.value, selecaoMandanteId: "", selecaoVisitanteId: "" })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="select-grupo"
              required
            >
              <option value="">Selecione</option>
              {gruposOrdenados.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Rodada</label>
            <input
              type="number"
              min={1}
              value={form.rodada}
              onChange={(e) => setForm({ ...form, rodada: e.target.value })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="input-rodada"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Mandante</label>
            <select
              value={form.selecaoMandanteId}
              onChange={(e) => setForm({ ...form, selecaoMandanteId: e.target.value })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="select-mandante"
              required
            >
              <option value="">Selecione</option>
              {selecoesGrupo.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Visitante</label>
            <select
              value={form.selecaoVisitanteId}
              onChange={(e) => setForm({ ...form, selecaoVisitanteId: e.target.value })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="select-visitante"
              required
            >
              <option value="">Selecione</option>
              {selecoesGrupo.filter((s) => s.id !== form.selecaoMandanteId).map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
            Data e horário (America/Sao_Paulo)
          </label>
          <input
            type="datetime-local"
            value={form.dataHoraJogo}
            onChange={(e) => setForm({ ...form, dataHoraJogo: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
            data-testid="input-dataHora"
            required
          />
          <div className="text-[10px] text-white/40 mt-1">Limite de palpite: 2h antes (automático)</div>
        </div>

        <button
          type="submit"
          disabled={enviando}
          className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-salvar-jogo"
        >
          {enviando ? "Salvando..." : "Criar Jogo"}
        </button>
      </form>
    </div>
  );
}

function ModalResultado({ jogo, onFechar, onSalvo }) {
  const [gm, setGm] = useState(jogo.golsMandante ?? 0);
  const [gv, setGv] = useState(jogo.golsVisitante ?? 0);
  const [enviando, setEnviando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post(`/resultados/jogos/${jogo.id}`, {
        golsMandante: Number(gm),
        golsVisitante: Number(gv),
      });
      toast.success("Resultado lançado e pontuação recalculada");
      onSalvo();
    } catch (e) { toast.error(formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-md w-full relative" data-testid="modal-resultado">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Lançar Resultado</div>
        <h2 className="font-heading uppercase font-bold text-2xl mt-1">
          {jogo.selecaoMandante?.nome} <span className="text-white/40">×</span> {jogo.selecaoVisitante?.nome}
        </h2>

        <div className="mt-6 grid grid-cols-3 items-center gap-3">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold mb-1">{jogo.selecaoMandante?.sigla}</div>
            <input type="number" min={0} value={gm} onChange={(e) => setGm(e.target.value)} className="w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-3 text-center font-heading font-black text-3xl" data-testid="input-gm-resultado" />
          </div>
          <div className="text-center font-heading font-black text-3xl text-white/40">×</div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold mb-1">{jogo.selecaoVisitante?.sigla}</div>
            <input type="number" min={0} value={gv} onChange={(e) => setGv(e.target.value)} className="w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-3 text-center font-heading font-black text-3xl" data-testid="input-gv-resultado" />
          </div>
        </div>

        <button
          type="submit"
          disabled={enviando}
          className="mt-6 w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-confirmar-resultado"
        >
          {enviando ? "Salvando..." : "Confirmar Resultado"}
        </button>
      </form>
    </div>
  );
}

function ModalEditarJogo({ jogo, selecoes, grupos, onFechar, onSalvo }) {
  const gruposOrdenados = useMemo(() => {
    return [...grupos].sort((a, b) => {
      const nomeA = a.nome.replace("Grupo ", "").trim();
      const nomeB = b.nome.replace("Grupo ", "").trim();
      return nomeA.localeCompare(nomeB);
    });
  }, [grupos]);

  const converterParaLocalDateTime = (isoString) => {
    if (!isoString) return "";
    const dt = new Date(isoString);
    const ano = dt.getFullYear();
    const mes = String(dt.getMonth() + 1).padStart(2, "0");
    const dia = String(dt.getDate()).padStart(2, "0");
    const hora = String(dt.getHours()).padStart(2, "0");
    const minuto = String(dt.getMinutes()).padStart(2, "0");
    return `${ano}-${mes}-${dia}T${hora}:${minuto}`;
  };

  const [form, setForm] = useState({
    grupoId: jogo.grupoId || "",
    rodada: jogo.rodada || 1,
    selecaoMandanteId: jogo.selecaoMandanteId || "",
    selecaoVisitanteId: jogo.selecaoVisitanteId || "",
    dataHoraJogo: converterParaLocalDateTime(jogo.dataHoraJogo),
  });
  const [enviando, setEnviando] = useState(false);

  const selecoesGrupo = useMemo(
    () => selecoes.filter((s) => !form.grupoId || s.grupoId === form.grupoId),
    [selecoes, form.grupoId]
  );

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      // O input datetime-local retorna YYYY-MM-DDTHH:mm em horário local (Brasília)
      // Enviar como ISO string com timezone Brasília (UTC-3)
      const [dataParte, horaParte] = form.dataHoraJogo.split("T");
      const [ano, mes, dia] = dataParte.split("-");
      const [hora, minuto] = horaParte.split(":");
      
      // Criar ISO string em formato Brasília (UTC-3)
      const isoString = `${ano}-${mes}-${dia}T${hora}:${minuto}:00-03:00`;
      
      await api.patch(`/jogos/${jogo.id}`, {
        grupoId: form.grupoId,
        rodada: Number(form.rodada),
        selecaoMandanteId: form.selecaoMandanteId,
        selecaoVisitanteId: form.selecaoVisitanteId,
        dataHoraJogo: isoString,
      });
      toast.success("Jogo atualizado");
      onSalvo();
    } catch (e) { toast.error(formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-lg w-full relative space-y-4" data-testid="modal-editar-jogo">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <h2 className="font-heading uppercase font-bold text-2xl">Editar Jogo</h2>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Grupo</label>
            <select
              value={form.grupoId}
              onChange={(e) => setForm({ ...form, grupoId: e.target.value, selecaoMandanteId: "", selecaoVisitanteId: "" })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="select-grupo-editar"
              required
            >
              <option value="">Selecione</option>
              {gruposOrdenados.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Rodada</label>
            <input
              type="number"
              min={1}
              value={form.rodada}
              onChange={(e) => setForm({ ...form, rodada: e.target.value })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="input-rodada-editar"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Mandante</label>
            <select
              value={form.selecaoMandanteId}
              onChange={(e) => setForm({ ...form, selecaoMandanteId: e.target.value })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="select-mandante-editar"
              required
            >
              <option value="">Selecione</option>
              {selecoesGrupo.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Visitante</label>
            <select
              value={form.selecaoVisitanteId}
              onChange={(e) => setForm({ ...form, selecaoVisitanteId: e.target.value })}
              className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
              data-testid="select-visitante-editar"
              required
            >
              <option value="">Selecione</option>
              {selecoesGrupo.filter((s) => s.id !== form.selecaoMandanteId).map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
            Data e horário (America/Sao_Paulo)
          </label>
          <input
            type="datetime-local"
            value={form.dataHoraJogo}
            onChange={(e) => setForm({ ...form, dataHoraJogo: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-3 py-2.5 text-sm"
            data-testid="input-dataHora-editar"
            required
          />
        </div>

        <button
          type="submit"
          disabled={enviando}
          className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-salvar-jogo-editar"
        >
          {enviando ? "Salvando..." : "Atualizar Jogo"}
        </button>
      </form>
    </div>
  );
}
